import { CollectionTreeQuery } from 'lib-knowledge-management-common/graphql';
import {
  Article,
  Category,
} from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { useMemo } from 'react';

import { MyNodeModel } from './useDragAndDrop';

// Need depth passed to reducers so we can limit category depth.
type Accumulator = {
  list: MyNodeModel[];
  depth: number;
};

function reduceCategory(
  acc: Accumulator,
  category: Category,
  index: number,
): Accumulator {
  acc.list.push({
    id: category.id,
    parent:
      category.parent?.__typename === 'Collection'
        ? 0
        : category.parent?.id || 0,
    text: extractTitleFromTranslations(category.titles),
    droppable: true,
    data: {
      node: category,
      index,
      depth: acc.depth,
    },
  });

  category.categories?.reduce(reduceCategory, { ...acc, depth: acc.depth + 1 });
  category.articles?.reduce(reduceArticle, acc);
  return acc;
}

function reduceArticle(
  acc: Accumulator,
  article: Article,
  index: number,
): Accumulator {
  acc.list.push({
    id: article.id,
    parent: article.parent!.id,
    text: extractTitleFromTranslations(article.titles),
    droppable: false,
    data: {
      node: article,
      index,
      depth: acc.depth,
    },
  });
  return acc;
}

function getTreeData(
  collection: NonNullable<CollectionTreeQuery['collection']>,
): MyNodeModel[] {
  const acc: Accumulator = {
    list: [],
    depth: 1,
  };
  if (!collection.categories) return [];
  return collection.categories.reduce(reduceCategory, acc).list;
}

export function useReduceTreeData(
  collectionQuery: CollectionTreeQuery | undefined,
): MyNodeModel[] {
  return useMemo(() => {
    if (!collectionQuery?.collection) return [];
    return getTreeData(collectionQuery.collection);
  }, [collectionQuery]);
}
